// src/components/LoadWatchPage.js
//
// Every load your dispatchers have looked at on Central Dispatch, Super
// Dispatch and ACV, merged into one list, with the driver each one suits.
//
// Nothing here searches a board. The browser extension reads what a dispatcher
// already has on screen while they are looking at it, and this page shows what
// it collected — the same car seen on two boards appears once.
//
// Matched loads come first, closest driver first: that is the order a
// dispatcher would want to make phone calls in.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase';

const BOARD_NAMES = {
  central_dispatch: 'Central Dispatch',
  super_dispatch: 'Super Dispatch',
  acv_transport: 'ACV',
};

const WINDOWS = [
  { hours: 4, label: 'Last 4 hours' },
  { hours: 24, label: 'Last 24 hours' },
  { hours: 72, label: 'Last 3 days' },
];

const place = (p) => (p ? (p.raw || [p.city, p.state].filter(Boolean).join(', ')) : '—');
const money = (n) => (n == null ? '—' : `$${Number(n).toLocaleString()}`);

function ago(iso) {
  if (!iso) return '';
  const mins = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  const h = Math.round(mins / 60);
  return h < 24 ? `${h} h ago` : `${Math.round(h / 24)} d ago`;
}

function vehicleLabel(load) {
  const v = load.vehicles?.[0];
  const one = v ? [v.year, v.make, v.model].filter(Boolean).join(' ') : '';
  const more = (load.vehicleCount || 0) > 1 ? ` +${load.vehicleCount - 1}` : '';
  return one ? one + more : (load.vehicleCount ? `${load.vehicleCount} vehicle(s)` : '—');
}

export default function LoadWatchPage() {
  const [hours, setHours] = useState(24);
  const [onlyMatched, setOnlyMatched] = useState(false);
  const [loads, setLoads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [loadedAt, setLoadedAt] = useState(null);

  const fetchLoads = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await httpsCallable(functions, 'listMarketLoads')({ hours });
      setLoads(res.data?.loads || []);
      setLoadedAt(new Date());
    } catch (e) {
      setError(e.message || 'Could not load the board.');
    } finally {
      setLoading(false);
    }
  }, [hours]);

  useEffect(() => { fetchLoads(); }, [fetchLoads]);

  // The extension adds rows all day; a quiet refresh keeps this current
  // without anyone having to remember to reload.
  useEffect(() => {
    const t = setInterval(fetchLoads, 60000);
    return () => clearInterval(t);
  }, [fetchLoads]);

  const shown = useMemo(
    () => (onlyMatched ? loads.filter((l) => l.matches?.length) : loads),
    [loads, onlyMatched]
  );
  const matchedCount = loads.filter((l) => l.bestMatchTier === 'close').length;

  return (
    <div className="p-4 md:p-6 max-w-7xl mx-auto">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Load Watch</h1>
          <p className="text-sm text-gray-600 mt-1">
            Loads your dispatchers have seen on the boards, matched to drivers who need work.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <select
            value={hours}
            onChange={(e) => setHours(Number(e.target.value))}
            className="border border-gray-300 rounded-md px-3 py-1.5 text-sm"
          >
            {WINDOWS.map((w) => <option key={w.hours} value={w.hours}>{w.label}</option>)}
          </select>
          <label className="flex items-center gap-2 text-sm text-gray-700 px-2">
            <input type="checkbox" checked={onlyMatched} onChange={(e) => setOnlyMatched(e.target.checked)} />
            Matched only
          </label>
          <button
            onClick={fetchLoads}
            className="px-3 py-1.5 text-sm rounded-md bg-blue-600 text-white hover:bg-blue-700"
          >
            Refresh
          </button>
        </div>
      </div>

      <div className="flex flex-wrap gap-3 mb-4 text-sm">
        <span className="px-3 py-1 rounded-full bg-gray-100 text-gray-700">{loads.length} loads seen</span>
        <span className="px-3 py-1 rounded-full bg-green-100 text-green-800">{matchedCount} with a driver close by</span>
        {loadedAt && <span className="px-3 py-1 text-gray-500">Updated {ago(loadedAt.toISOString())}</span>}
      </div>

      {error && (
        <div className="mb-4 p-3 rounded-md bg-red-50 text-red-700 text-sm">{error}</div>
      )}

      {!loading && !error && loads.length === 0 && (
        <div className="p-8 text-center rounded-lg border border-dashed border-gray-300 text-gray-600">
          <p className="font-medium">Nothing collected yet.</p>
          <p className="text-sm mt-1">
            Install the LoadMemo extension, add your API key, and open Central Dispatch,
            Super Dispatch or ACV. Loads appear here as dispatchers look at them.
          </p>
        </div>
      )}

      {shown.length > 0 && (
        <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500">
              <tr>
                <th className="px-3 py-2">Route</th>
                <th className="px-3 py-2">Vehicle</th>
                <th className="px-3 py-2 text-right">Price</th>
                <th className="px-3 py-2 text-right">$/mi</th>
                <th className="px-3 py-2">Best driver</th>
                <th className="px-3 py-2">Board</th>
                <th className="px-3 py-2">Seen</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {shown.map((l) => {
                const best = l.matches?.[0];
                const close = l.bestMatchTier === 'close';
                return (
                  <tr key={l.id} className={close ? 'bg-green-50' : ''}>
                    <td className="px-3 py-2">
                      <div className="font-medium text-gray-900">{place(l.origin)}</div>
                      <div className="text-gray-500">→ {place(l.destination)}</div>
                    </td>
                    <td className="px-3 py-2 text-gray-700">{vehicleLabel(l)}</td>
                    <td className="px-3 py-2 text-right font-semibold tabular-nums">{money(l.price)}</td>
                    <td className="px-3 py-2 text-right text-gray-600 tabular-nums">
                      {l.pricePerMile != null ? `$${l.pricePerMile}` : '—'}
                    </td>
                    <td className="px-3 py-2">
                      {best ? (
                        <div>
                          <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold ${close ? 'bg-green-600 text-white' : 'bg-gray-200 text-gray-700'}`}>
                            {close ? 'Close' : 'In range'}
                          </span>
                          <div className="text-gray-700 mt-1">{best.reason}</div>
                          {l.matches.length > 1 && (
                            <div className="text-xs text-gray-500">+{l.matches.length - 1} more driver(s)</div>
                          )}
                        </div>
                      ) : (
                        <span className="text-gray-400">No driver nearby</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-gray-700">
                      {l.sourceUrl ? (
                        <a href={l.sourceUrl} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline">
                          {BOARD_NAMES[l.source] || l.source}
                        </a>
                      ) : (BOARD_NAMES[l.source] || l.source)}
                    </td>
                    <td className="px-3 py-2 text-gray-500 whitespace-nowrap">{ago(l.lastSeenAt)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {loading && loads.length === 0 && <div className="p-8 text-center text-gray-500">Loading…</div>}
    </div>
  );
}
